/**
 * Batch Export Panel
 * Select multiple ad formats and export all at once
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Download,
  Check,
  Square,
  CheckSquare,
  ChevronRight,
  X,
  Loader2,
  Package,
} from 'lucide-react';
import { useEditorStore, useTemplateStore, useAnimationStore } from '@/stores';
import { PROVIDERS } from '@/components/providers';
import type { AdFormat, Provider } from '@/types/formats';
import type { ExportFormat } from '@/types';

interface BatchExportJob {
  format: AdFormat;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
}

interface BatchExportPanelProps {
  selectedExportFormat: ExportFormat;
  scale: number;
  quality: number;
}

export function BatchExportPanel({
  selectedExportFormat,
  scale,
  quality,
}: BatchExportPanelProps) {
  const { selectedFormat, setFormat } = useEditorStore();
  const { currentTemplate, variableValues } = useTemplateStore();
  const { seek, stop } = useAnimationStore();

  const [selectedFormats, setSelectedFormats] = useState<Set<string>>(new Set());
  const [expandedProviders, setExpandedProviders] = useState<Set<Provider>>(new Set(['facebook']));
  const [isExporting, setIsExporting] = useState(false);
  const [jobs, setJobs] = useState<BatchExportJob[]>([]);
  const [currentJobIndex, setCurrentJobIndex] = useState(0);

  // All available formats
  const allFormats = useMemo(() => {
    return PROVIDERS.flatMap(p => p.formats);
  }, []);

  const toggleProvider = (providerId: Provider) => {
    setExpandedProviders(prev => {
      const next = new Set(prev);
      if (next.has(providerId)) {
        next.delete(providerId);
      } else {
        next.add(providerId);
      }
      return next;
    });
  };

  const toggleFormat = (formatId: string) => {
    setSelectedFormats(prev => {
      const next = new Set(prev);
      if (next.has(formatId)) {
        next.delete(formatId);
      } else {
        next.add(formatId);
      }
      return next;
    });
  };

  const selectAllFromProvider = (providerId: Provider) => {
    const provider = PROVIDERS.find(p => p.id === providerId);
    if (!provider) return;

    setSelectedFormats(prev => {
      const next = new Set(prev);
      const allSelected = provider.formats.every(f => next.has(f.id));

      if (allSelected) {
        // Deselect all
        provider.formats.forEach(f => next.delete(f.id));
      } else {
        // Select all
        provider.formats.forEach(f => next.add(f.id));
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedFormats(new Set());
  };

  // Export a single format
  const exportSingleFormat = useCallback(async (
    format: AdFormat,
    exportFormat: ExportFormat,
    templateName: string
  ): Promise<Blob> => {
    // Change to this format temporarily
    setFormat(format);

    // Wait for render
    await new Promise(resolve => setTimeout(resolve, 100));

    const canvas = document.getElementById('ad-canvas');
    if (!canvas) throw new Error('Canvas not found');

    let filename = `mooncloak-${format.id}-${templateName}`;
    let blob: Blob;

    if (exportFormat === 'svg') {
      const { exportToSVG } = await import('@/lib/export');
      const svgString = exportToSVG(
        currentTemplate!.layers,
        format,
        currentTemplate!.canvas.background,
        variableValues
      );
      blob = new Blob([svgString], { type: 'image/svg+xml' });
      filename += '.svg';
    } else {
      // Raster formats
      const html2canvas = (await import('html2canvas')).default;
      const capturedCanvas = await html2canvas(canvas, {
        scale,
        backgroundColor: null,
        logging: false,
        useCORS: true,
      });

      if (exportFormat === 'png') {
        blob = await new Promise<Blob>((resolve) =>
          capturedCanvas.toBlob((b) => resolve(b!), 'image/png')
        );
        filename += '.png';
      } else if (exportFormat === 'jpeg') {
        blob = await new Promise<Blob>((resolve) =>
          capturedCanvas.toBlob((b) => resolve(b!), 'image/jpeg', quality)
        );
        filename += '.jpg';
      } else if (exportFormat === 'webp') {
        blob = await new Promise<Blob>((resolve) =>
          capturedCanvas.toBlob((b) => resolve(b!), 'image/webp', quality)
        );
        filename += '.webp';
      } else if (exportFormat === 'pdf') {
        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({
          orientation: format.width > format.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [format.width * scale, format.height * scale],
        });
        const imgData = capturedCanvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', 0, 0, format.width * scale, format.height * scale);
        blob = pdf.output('blob');
        filename += '.pdf';
      } else {
        throw new Error(`Unsupported format: ${exportFormat}`);
      }
    }

    // Download the file
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return blob;
  }, [currentTemplate, variableValues, scale, quality, setFormat]);

  const handleBatchExport = useCallback(async () => {
    if (isExporting || selectedFormats.size === 0 || !currentTemplate) return;

    setIsExporting(true);
    const originalFormat = selectedFormat;

    // Get selected format objects
    const formatsToExport = allFormats.filter(f => selectedFormats.has(f.id));
    const templateName = currentTemplate.metadata.name.toLowerCase().replace(/\s+/g, '-') || 'design';

    // Initialize jobs
    const initialJobs: BatchExportJob[] = formatsToExport.map(format => ({
      format,
      status: 'pending',
    }));
    setJobs(initialJobs);
    setCurrentJobIndex(0);

    // Stop any animations
    stop();

    // Export each format
    for (let i = 0; i < formatsToExport.length; i++) {
      const format = formatsToExport[i];
      setCurrentJobIndex(i);

      // Update status to processing
      setJobs(prev => prev.map((job, idx) =>
        idx === i ? { ...job, status: 'processing' } : job
      ));

      try {
        await exportSingleFormat(format, selectedExportFormat, templateName);

        // Update status to completed
        setJobs(prev => prev.map((job, idx) =>
          idx === i ? { ...job, status: 'completed' } : job
        ));
      } catch (error) {
        // Update status to failed
        setJobs(prev => prev.map((job, idx) =>
          idx === i ? { ...job, status: 'failed', error: (error as Error).message } : job
        ));
      }

      // Small delay between exports
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Restore original format
    setFormat(originalFormat);
    setIsExporting(false);
  }, [
    isExporting,
    selectedFormats,
    currentTemplate,
    selectedFormat,
    allFormats,
    selectedExportFormat,
    exportSingleFormat,
    setFormat,
    stop,
  ]);

  const completedCount = jobs.filter(j => j.status === 'completed').length;
  const failedCount = jobs.filter(j => j.status === 'failed').length;

  return (
    <div className="border-t border-white/10 pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="label mb-0 flex items-center gap-2">
          <Package size={14} />
          Batch Export
        </h3>
        {selectedFormats.size > 0 && (
          <button
            onClick={clearSelection}
            className="text-xs text-fg-subtle hover:text-fg"
          >
            Clear ({selectedFormats.size})
          </button>
        )}
      </div>

      <p className="text-xs text-fg-subtle mb-3">
        Select multiple ad sizes to export all at once.
      </p>

      {/* Format selection */}
      <div className="max-h-48 overflow-y-auto mb-4 border border-white/10 rounded-lg">
        {PROVIDERS.map((provider) => {
          const providerSelectedCount = provider.formats.filter(
            f => selectedFormats.has(f.id)
          ).length;
          const allSelected = providerSelectedCount === provider.formats.length;

          return (
            <div key={provider.id} className="border-b border-white/5 last:border-b-0">
              <div className="flex items-center">
                <button
                  onClick={() => selectAllFromProvider(provider.id)}
                  className="p-2 text-fg-muted hover:text-fg"
                  title={allSelected ? 'Deselect all' : 'Select all'}
                >
                  {allSelected ? (
                    <CheckSquare size={14} className="text-primary" />
                  ) : providerSelectedCount > 0 ? (
                    <CheckSquare size={14} className="text-primary/50" />
                  ) : (
                    <Square size={14} />
                  )}
                </button>
                <button
                  onClick={() => toggleProvider(provider.id)}
                  className="flex-1 flex items-center gap-2 py-2 pr-3 text-left hover:bg-white/5"
                >
                  <ChevronRight
                    size={12}
                    className={`transition-transform ${
                      expandedProviders.has(provider.id) ? 'rotate-90' : ''
                    }`}
                  />
                  <provider.icon size={12} />
                  <span className="text-xs font-medium">{provider.name}</span>
                  {providerSelectedCount > 0 && (
                    <span className="text-[10px] text-primary ml-auto">
                      {providerSelectedCount}/{provider.formats.length}
                    </span>
                  )}
                </button>
              </div>

              {expandedProviders.has(provider.id) && (
                <div className="pl-6 pb-2">
                  {provider.formats.map((format) => (
                    <button
                      key={format.id}
                      onClick={() => toggleFormat(format.id)}
                      className={`
                        w-full flex items-center gap-2 px-2 py-1.5 text-left rounded
                        ${selectedFormats.has(format.id)
                          ? 'bg-primary/10 text-fg'
                          : 'text-fg-muted hover:bg-white/5 hover:text-fg'
                        }
                      `}
                    >
                      {selectedFormats.has(format.id) ? (
                        <CheckSquare size={12} className="text-primary" />
                      ) : (
                        <Square size={12} />
                      )}
                      <span className="text-xs flex-1 truncate">{format.name}</span>
                      <span className="text-[10px] text-fg-subtle">
                        {format.width}x{format.height}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Export button */}
      <button
        onClick={handleBatchExport}
        disabled={isExporting || selectedFormats.size === 0}
        className="w-full btn-primary py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {isExporting ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Exporting {currentJobIndex + 1} of {jobs.length}...
          </>
        ) : (
          <>
            <Download size={16} />
            Export {selectedFormats.size} Format{selectedFormats.size !== 1 ? 's' : ''}
          </>
        )}
      </button>

      {/* Progress */}
      {jobs.length > 0 && (
        <div className="mt-3 space-y-1">
          {isExporting && (
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${((currentJobIndex + 1) / jobs.length) * 100}%` }}
              />
            </div>
          )}

          {!isExporting && (completedCount > 0 || failedCount > 0) && (
            <div className="flex items-center gap-3 text-xs">
              {completedCount > 0 && (
                <span className="text-green-400 flex items-center gap-1">
                  <Check size={12} />
                  {completedCount} exported
                </span>
              )}
              {failedCount > 0 && (
                <span className="text-red-400 flex items-center gap-1">
                  <X size={12} />
                  {failedCount} failed
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
